import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  Alert,
  DataTable,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
  StatusBadge,
  Tabs,
} from '@/components/ui'
import { abrirFolha, fetchBeneficiosRelatorio, fetchFolhas } from './api'
import { formatCompetencia, formatMoney } from './utils'

type Tab = 'ferias' | 'decimo' | 'reajuste' | 'beneficios' | 'folha'

function competenciaAtual() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export function MassaPage() {
  const { canEdit } = useAuth()
  const editable = canEdit('dp-massa') || canEdit('dp-workflow-folha')
  const qc = useQueryClient()
  const [tab, setTab] = useState<Tab>('ferias')
  const [competencia, setCompetencia] = useState(competenciaAtual)
  const [msg, setMsg] = useState('')
  const [msgTone, setMsgTone] = useState<'success' | 'danger' | 'info'>('info')

  const folhas = useQuery({
    queryKey: ['dp', 'folhas'],
    queryFn: fetchFolhas,
    enabled: tab === 'folha' || tab === 'decimo',
  })

  const relBenef = useQuery({
    queryKey: ['dp', 'beneficios', 'relatorio'],
    queryFn: fetchBeneficiosRelatorio,
    enabled: tab === 'beneficios',
  })

  const abrirMut = useMutation({
    mutationFn: () => abrirFolha(competencia),
    onSuccess: async (res) => {
      setMsgTone('success')
      setMsg(res.mensagem || `Folha ${competencia} ${res.criada ? 'aberta' : 'já existia'}.`)
      await qc.invalidateQueries({ queryKey: ['dp', 'folhas'] })
      await qc.invalidateQueries({ queryKey: ['dp', 'dashboard'] })
    },
    onError: (err) => {
      setMsgTone('danger')
      setMsg(err instanceof ApiError ? err.message : 'Falha ao abrir folha.')
    },
  })

  const folhaLista = folhas.data?.dados || []
  const custoEmpresa = Number(
    (relBenef.data?.dados as { custo_empresa?: number } | undefined)?.custo_empresa ??
      (relBenef.data?.dados as { total_empresa?: number } | undefined)?.total_empresa ??
      0,
  )

  return (
    <section className="hi-page">
      <PageHeader
        title="Cálculo em massa"
        subtitle="Férias coletivas, 13º, reajuste e benefícios em lote — processamento avançado no legado."
        actions={
          <a className="btn btn-primary" href={legacyAppUrl('#/dp-massa')}>
            Processar no legado
          </a>
        }
      />

      {msg ? <Alert tone={msgTone}>{msg}</Alert> : null}

      <Tabs
        items={[
          { id: 'ferias', label: 'Férias coletivas' },
          { id: 'decimo', label: '13º' },
          { id: 'reajuste', label: 'Reajuste' },
          { id: 'beneficios', label: 'Benefícios' },
          { id: 'folha', label: 'Folha' },
        ]}
        value={tab}
        onChange={(id) => setTab(id as Tab)}
        label="Tipo de lote"
      />

      {tab === 'ferias' ? (
        <Section title="Férias coletivas">
          <Alert tone="info">
            Use a fila enterprise para aprovar/agendar e o legado para fechar gozo coletivo em
            lote.
          </Alert>
          <div className="hi-filters">
            <Link className="btn btn-primary" to="/app/dp/ferias">
              Abrir férias SPA
            </Link>
            <a className="btn btn-secondary" href={legacyAppUrl('#/dp-ferias-enterprise')}>
              Enterprise legado
            </a>
          </div>
        </Section>
      ) : null}

      {tab === 'decimo' ? (
        <Section title="13º coletivo">
          <Muted>
            A SPA abre/calcula a folha da competência; o demonstrativo coletivo de 13º permanece
            no legado.
          </Muted>
          <form
            className="hi-filters"
            onSubmit={(e) => {
              e.preventDefault()
              if (editable) abrirMut.mutate()
            }}
          >
            <input
              type="month"
              value={competencia}
              onChange={(e) => setCompetencia(e.target.value)}
              aria-label="Competência"
            />
            {editable ? (
              <button type="submit" className="btn btn-primary" disabled={abrirMut.isPending}>
                Abrir folha da competência
              </button>
            ) : null}
            <a className="btn btn-secondary" href={legacyAppUrl('#/dp-massa')}>
              13º no legado
            </a>
          </form>
          {folhaLista.length > 0 ? (
            <DataTable>
              <thead>
                <tr>
                  <th>Competência</th>
                  <th>Status</th>
                  <th>Líquido</th>
                </tr>
              </thead>
              <tbody>
                {folhaLista.slice(0, 6).map((f) => (
                  <tr key={f.id}>
                    <td>{formatCompetencia(f.competencia)}</td>
                    <td>
                      <StatusBadge
                        tone={
                          /aprov|pag/i.test(String(f.status))
                            ? 'ativo'
                            : /calc/i.test(String(f.status))
                              ? 'pendente'
                              : 'pendente'
                        }
                      >
                        {f.status_display || f.status || '—'}
                      </StatusBadge>
                    </td>
                    <td>{formatMoney(f.total_liquido)}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          ) : null}
        </Section>
      ) : null}

      {tab === 'reajuste' ? (
        <Section title="Reajuste em massa">
          <Alert tone="info">
            Simulação e aplicação de percentual com histórico salarial ficam no legado. Na SPA,
            consulte o histórico funcional do colaborador.
          </Alert>
          <div className="hi-filters">
            <Link className="btn btn-secondary" to="/app/dp/historico">
              Histórico funcional
            </Link>
            <a className="btn btn-primary" href={legacyAppUrl('#/dp-massa')}>
              Simular reajuste
            </a>
          </div>
        </Section>
      ) : null}

      {tab === 'beneficios' ? (
        <Section title="Benefícios em lote">
          {relBenef.isLoading ? <Muted>Carregando relatório…</Muted> : null}
          <KpiGrid>
            <Kpi
              label="Custo empresa (relatório)"
              value={custoEmpresa ? formatMoney(custoEmpresa) : '—'}
            />
          </KpiGrid>
          <div className="hi-filters">
            <Link className="btn btn-primary" to="/app/dp/beneficios">
              Política / atribuir
            </Link>
            <a className="btn btn-secondary" href={legacyAppUrl('#/dp-massa')}>
              Lote no legado
            </a>
          </div>
        </Section>
      ) : null}

      {tab === 'folha' ? (
        <Section title="Abrir competência em lote" flush>
          <form
            className="hi-filters"
            style={{ padding: 16 }}
            onSubmit={(e) => {
              e.preventDefault()
              if (editable) abrirMut.mutate()
            }}
          >
            <input
              type="month"
              value={competencia}
              onChange={(e) => setCompetencia(e.target.value)}
              aria-label="Competência"
              required
            />
            {editable ? (
              <button type="submit" className="btn btn-primary" disabled={abrirMut.isPending}>
                Abrir / reabrir folha
              </button>
            ) : null}
            <Link className="btn btn-secondary" to="/app/dp">
              Painel DP
            </Link>
          </form>
          {folhas.isLoading ? <Muted>Carregando…</Muted> : null}
          {!folhas.isLoading && folhaLista.length === 0 ? (
            <Empty>Nenhuma folha ainda.</Empty>
          ) : null}
          {folhaLista.length > 0 ? (
            <DataTable>
              <thead>
                <tr>
                  <th>Competência</th>
                  <th>Status</th>
                  <th>Colaboradores</th>
                  <th>Bruto</th>
                  <th>Líquido</th>
                </tr>
              </thead>
              <tbody>
                {folhaLista.map((f) => (
                  <tr key={f.id}>
                    <td>{formatCompetencia(f.competencia)}</td>
                    <td>{f.status_display || f.status || '—'}</td>
                    <td>{f.qtd_colaboradores ?? '—'}</td>
                    <td>{formatMoney(f.total_bruto)}</td>
                    <td>{formatMoney(f.total_liquido)}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          ) : null}
        </Section>
      ) : null}
    </section>
  )
}

export default MassaPage
